import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { FormControl, Select, MenuItem } from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge, EmptyState } from "../../components/ui";
import { FilterBar, SearchField, TableCard, RowActions } from "../../components/FormFields/panel";
import { getTokens, brandAlpha } from "../../styles/tokens";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

// TODO i18n: chaves `tagsKanban.*` novas com defaultValue (i18next aceita nativamente)
const t = (key, fallback) => i18n.t(key, { defaultValue: fallback });

const reducer = (state, action) => {
  if (action.type === "LOAD_TAGS") {
    const tags = action.payload;
    const newTags = [];

    tags.forEach((tag) => {
      const tagIndex = state.findIndex((s) => s.id === tag.id);
      if (tagIndex !== -1) {
        state[tagIndex] = tag;
      } else {
        newTags.push(tag);
      }
    });

    return [...state, ...newTags];
  }

  if (action.type === "UPDATE_TAGS") {
    const tag = action.payload;
    const tagIndex = state.findIndex((s) => s.id === tag.id);

    if (tagIndex !== -1) {
      state[tagIndex] = tag;
      return [...state];
    } else {
      return [tag, ...state];
    }
  }

  if (action.type === "DELETE_TAGS") {
    const tagId = action.payload;

    const tagIndex = state.findIndex((s) => s.id === tagId);
    if (tagIndex !== -1) {
      state.splice(tagIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    },
    pageSize: { minWidth: 150 },
    // etapa: pílula tingida com a cor do DADO + bolinha na cor cheia
    lanePill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      maxWidth: 320,
      padding: "2px 10px",
      borderRadius: tk.radius.badge,
      fontSize: 13,
      fontWeight: 600,
      color: tk.text,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    laneDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flex: "none",
    },
    swatch: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      color: tk.textSecondary,
      fontVariantNumeric: "tabular-nums",
      "& i": {
        width: 16,
        height: 16,
        borderRadius: 4,
        border: `1px solid ${tk.line}`,
        display: "inline-block",
      },
    },
    dangerBtn: {
      "&:hover": { color: `${tk.semantic.error} !important` },
    },
    loadMore: {
      display: "flex",
      justifyContent: "center",
      padding: 12,
    },
  };
});

const Tags = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTags = async () => {
        try {
          const { data } = await api.get("/tags/", {
            params: { searchParam, pageNumber, kanban: 1, limit: pageSize },
          });
          dispatch({ type: "LOAD_TAGS", payload: data.tags });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchTags();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, pageSize]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const onTagsEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onTagsEvent);

    return () => {
      socket.off(`company${user.companyId}-tag`, onTagsEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const handleOpenTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(true);
  };

  const handleCloseTagModal = () => {
    setSelectedTag(null);
    setTagModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setTagModalOpen(true);
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleReturnToKanban = () => {
    history.push("/kanban");
  };

  const laneColor = (tag) => tag.color || "#757575";

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTag && `${i18n.t("tagsKanban.confirmationModal.deleteTitle")}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tagsKanban.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={handleCloseTagModal}
          aria-labelledby="form-dialog-title"
          tagId={selectedTag && selectedTag.id}
          kanban={1}
        />
      )}
      <div className={classes.root}>
        <PageHeader
          title={i18n.t("tagsKanban.title")}
          subtitle={t(
            "tagsKanban.subtitle",
            "Cada etapa é uma coluna do Kanban; a cor identifica os cards no quadro."
          )}
          badge={<StatusBadge color="neutral" dot={false} label={String(tags.length)} />}
          actions={[
            <Button
              key="back"
              variant="outlined"
              onClick={handleReturnToKanban}
              startIcon={<ArrowBackIcon fontSize="small" />}
            >
              {t("tagsKanban.backToBoard", "Voltar ao Kanban")}
            </Button>,
            <Button
              key="add"
              variant="contained"
              color="primary"
              onClick={handleOpenTagModal}
              startIcon={<AddIcon fontSize="small" />}
            >
              {i18n.t("tagsKanban.buttons.add")}
            </Button>,
          ]}
        />

        <FilterBar>
          <SearchField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            value={searchParam}
            onChange={handleSearch}
          />
          <FormControl variant="outlined" size="small" className={classes.pageSize}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              renderValue={(value) => `${value} por página`}
              inputProps={{ "aria-label": "Itens por página" }}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </FilterBar>

        <TableCard fill onScroll={handleScroll}>
          {!loading && tags.length === 0 ? (
            <EmptyState
              icon={<ViewColumnOutlinedIcon />}
              title={t("tagsKanban.emptyTitle", "Nenhuma etapa configurada")}
              message={t(
                "tagsKanban.emptyMessage",
                "Crie a primeira etapa do funil para começar a organizar os atendimentos no Kanban."
              )}
              action={
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenTagModal}
                  startIcon={<AddIcon fontSize="small" />}
                >
                  {i18n.t("tagsKanban.buttons.add")}
                </Button>
              }
            />
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("tagsKanban.table.name")}</TableCell>
                  <TableCell>{i18n.t("tagsKanban.table.color")}</TableCell>
                  <TableCell>{i18n.t("tagsKanban.table.tickets")}</TableCell>
                  <TableCell align="right">
                    {i18n.t("tagsKanban.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.id}>
                    <TableCell>
                      <span
                        className={classes.lanePill}
                        style={{ backgroundColor: brandAlpha(laneColor(tag), 0.14) }}
                      >
                        <span
                          className={classes.laneDot}
                          style={{ backgroundColor: laneColor(tag) }}
                        />
                        {tag.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={classes.swatch}>
                        <i style={{ backgroundColor: laneColor(tag) }} />
                        {String(laneColor(tag)).toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        color="neutral"
                        dot={false}
                        size="sm"
                        label={String(tag?.ticketTags ? tag.ticketTags.length : 0)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <RowActions>
                        <Tooltip title={t("tagsKanban.edit", "Editar etapa")}>
                          <IconButton
                            size="small"
                            aria-label={`${t("tagsKanban.edit", "Editar etapa")} ${tag.name}`}
                            onClick={() => handleEditTag(tag)}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("tagsKanban.delete", "Excluir etapa")}>
                          <IconButton
                            size="small"
                            className={classes.dangerBtn}
                            aria-label={`${t("tagsKanban.delete", "Excluir etapa")} ${tag.name}`}
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingTag(tag);
                            }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
                {loading &&
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRowSkeleton key={`skeleton-${idx}`} columns={4} />
                  ))}
              </TableBody>
            </Table>
          )}
          {hasMore && (
            <div className={classes.loadMore}>
              <Button variant="outlined" onClick={loadMore} disabled={loading}>
                {loading ? i18n.t("contacts.loading") : i18n.t("contacts.loadMore")}
              </Button>
            </div>
          )}
        </TableCard>
      </div>
    </MainContainer>
  );
};

export default Tags;
